export interface ImageSrcSetOptions {
  heightRatio?: number;
  transformable?: boolean;
}

export type MediaSourceType = 'none' | 'image' | 'local' | 'youtube';

export interface MediaSourceBases {
  image: string;
  video: string;
}

export function isDirectMediaSource(source: string): boolean {
  return /^(https?:|data:|blob:|\/\/)/i.test(source);
}

export function resolveMediaUrl(base: string, source: string | null | undefined): string {
  const normalizedSource = String(source ?? '').trim();
  if (!normalizedSource) {
    return '';
  }

  if (isDirectMediaSource(normalizedSource) || normalizedSource.startsWith('/')) {
    return normalizedSource;
  }

  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return `${normalizedBase}${normalizedSource.replace(/^\/+/, '')}`;
}

export function resolveMediaSource(
  source: string | null | undefined,
  mediaType: MediaSourceType,
  bases: MediaSourceBases
): string {
  const normalizedSource = String(source ?? '').trim();
  if (!normalizedSource) {
    return '';
  }

  if (mediaType === 'none') {
    return '';
  }

  if (mediaType === 'youtube' || isDirectMediaSource(normalizedSource)) {
    return normalizedSource;
  }

  if (/^\/?assets\//i.test(normalizedSource)) {
    return normalizedSource.startsWith('/') ? normalizedSource : `/${normalizedSource}`;
  }

  const base = mediaType === 'local' ? bases.video : bases.image;
  const apiPrefix = mediaType === 'local' ? 'api/media/video/' : 'api/media/file/';
  const sourceWithoutLeadingSlash = normalizedSource.replace(/^\/+/, '');

  if (sourceWithoutLeadingSlash.startsWith(apiPrefix)) {
    return resolveMediaUrl(base, sourceWithoutLeadingSlash.slice(apiPrefix.length));
  }

  if (normalizedSource.startsWith('/')) {
    const segments = normalizedSource.split('/').filter(Boolean);
    return resolveMediaUrl(base, segments.at(-1) ?? '');
  }

  return resolveMediaUrl(base, normalizedSource);
}

export function appendImageTransform(
  source: string,
  width: number,
  height: number,
  transformable = true
): string {
  if (!source || !transformable || !canTransformImageSource(source)) {
    return source;
  }

  const hashIndex = source.indexOf('#');
  const url = hashIndex >= 0 ? source.slice(0, hashIndex) : source;
  const fragment = hashIndex >= 0 ? source.slice(hashIndex) : '';
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}width=${width}&height=${height}${fragment}`;
}

export function buildImageSrcSet(
  source: string,
  widths: readonly number[],
  options: ImageSrcSetOptions = {}
): string {
  const { heightRatio = 1, transformable = true } = options;

  if (!source || !transformable || !canTransformImageSource(source)) {
    return '';
  }

  const validWidths = [...new Set(widths)]
    .filter((width) => Number.isFinite(width) && width > 0)
    .map((width) => Math.round(width));

  return validWidths
    .map((width) => {
      const height = Math.max(1, Math.round(width * heightRatio));
      const candidate = appendImageTransform(source, width, height);
      return `${escapeSrcSetUrl(candidate)} ${width}w`;
    })
    .join(', ');
}

function canTransformImageSource(source: string): boolean {
  return !/^(data:|blob:)/i.test(source);
}

function escapeSrcSetUrl(url: string): string {
  return url.replace(/[\u0000-\u0020,]/g, (character) => encodeURIComponent(character));
}
